import { useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

import { identifyIntercomUser, resetIntercom } from './intercom';

/**
 * Passes the signed-in retailer's details to the Intercom messenger, so support
 * sees who is messaging instead of an anonymous visitor. Renders nothing.
 *
 * Mount it inside `ClerkProvider` but outside the router, so it covers the
 * signed-out pages too — the messenger is available on /login, where someone
 * who can't get in is most likely to need it.
 *
 * Impersonated sessions are skipped. HQ's "view as" mints a Clerk actor token,
 * so `act` is set on the claims; identifying there would post staff messages as
 * the retailer and write to their Intercom contact.
 *
 * @param {string} product - which dashboard this is, passed through to the
 *   contact as a custom attribute.
 */
export const IntercomIdentity = ({ product }) => {
	const { isLoaded, isSignedIn, sessionClaims } = useAuth();
	const { user } = useUser();

	const impersonated = Boolean(sessionClaims?.act);

	useEffect(() => {
		if (!isLoaded) return;

		if (!isSignedIn || impersonated) {
			resetIntercom();
			return;
		}

		if (user) identifyIntercomUser(user, product);
	}, [isLoaded, isSignedIn, impersonated, user, product]);

	return null;
};
